import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Button from '@components/button/Button'
import Drawer from '@components/drawer/Drawer'
import Header from '@components/header/Header'
import SortSelector from '@components/modal/SortSelector'
import { RadioOption } from '@components/radio/Radio'
import Modal from '@components/modal/Modal'
import Loader from '@components/loader/Loader'
import SimpleAlert from '@components/modal/SimpleAlert'

import UserCard from '@components/card/UserCard'
import UserListHeader from './UserListHeader'
import UserListFilters from './UserListFiltersWeb'
import UserFiltersMobile from './UserFiltersMobile'

import { filterUsers, ACTIVE_FILTER_ID, INACTIVE_FILTER_ID } from '@utils/admin'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as AdminStore from '@store/admin/admin'
import * as AuthStore from '@store/auth/auth'

import api from '@api/api'
import Logger from '@utils/logger'
import exporter from '@utils/exporter'
import config from '@app/config'

const USER_TYPES = ['VIPARIS', 'PRESTATAIRE', 'ORGANISATEUR', 'EXPOSANT'] as const

const renderBottomButton = (button: HeaderButton) => (
  <Footer>
    <Button label={button.label} onClick={button.onClick} status="active" />
  </Footer>
)

const UserListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const initialFilters: UserFilters = {
    site: [],
    manifestation: [],
    typeUtilisateur: [],
    role: [],
    status: [],
    prestataire: [],
  }

  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const users = useReducer(AdminStore.store, (s) => s.users)
  const services = useReducer(AdminStore.store, (s) => s.services)
  const manifestations = useReducer(AdminStore.store, (s) => s.manifestations)
  const roles = useReducer(AdminStore.store, (s) => s.roles)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('loading')
  const [exporting, setExporting] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [filters, setFilters] = React.useState<UserFilters>(initialFilters)
  const [isFilterOpen, setIsFilterOpen] = React.useState(false)
  const [isHoverFilter, setIsHoverFilter] = React.useState(false)

  const sorts: RadioOption[] = [
    { label: i18n.t('screens.admin.users.sorts.name'), value: 'name' },
    { label: i18n.t('screens.admin.users.sorts.createdAt'), value: 'createdAt' },
  ]

  const [reverse, setReverse] = React.useState(false)
  const [sort, setSort] = React.useState<RadioOption | undefined>(sorts[0])

  React.useEffect(() => {
    api.users
      .all()
      .then((us) => AdminStore.actions.setUsers(us))
      .catch(Logger.error)
      .finally(() => setStatus('fetched'))

    Promise.all([api.site.all(), api.societe.all(), api.manifestation.all(), api.roles.all()])
      .then(([sitesRes, socsRes, manifRes, roleRes]) => {
        SiteStore.actions.setSites(sitesRes)
        AdminStore.actions.setServices(socsRes)
        AdminStore.actions.setManifestations(manifRes)
        AdminStore.actions.setRoles(roleRes)
      })
      .catch(Logger.error)
  }, [])

  const onScreenClick = () => {
    if (!isHoverFilter) {
      setIsFilterOpen(false)
    }
  }

  React.useEffect(() => {
    if (isFilterOpen) {
      window.addEventListener('click', onScreenClick)
      return () => window.removeEventListener('click', onScreenClick)
    }
  }, [isFilterOpen, isHoverFilter])

  const displayedData = React.useMemo(() => {
    const filtered = filterUsers(users, search, filters)

    const sorted = sort
      ? filtered.sort(
          (a, b) =>
            (sort.value === 'name' ? a.nom.localeCompare(b.nom) : a.creationDate.localeCompare(b.creationDate)) *
            (reverse ? -1 : 1)
        )
      : filtered

    return sorted
  }, [users, services, sort, reverse, search, filters])

  React.useEffect(() => {
    if (exporting) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({ Content: () => <Loader />, position: 'center' })
        }
        exporter
          .download<UsersExportRequest>(
            config.ws.users.export,
            `export_utilisateurs.xlsx`,
            'application/vnd.ms-excel',
            { userIds: displayedData.map((data) => data.id) }
          )
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.admin.users.export.success.title'),
                description: i18n.t('screens.admin.users.export.success.description'),
                buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
              })
            }
          })
          .catch((err) => {
            Logger.error('Unable to download ', err)
            SimpleAlert.open({
              title: i18n.t('screens.admin.users.export.error.title'),
              description: i18n.t('screens.admin.users.export.error.description'),
              buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExporting(false)
          })
      }
    }
  }, [exporting])

  const numberOfFilters = React.useMemo(
    () => Object.keys(filters).reduce((acc, cur) => acc + filters[cur as UserFiltersName].length, 0),
    [filters]
  )

  const updateFilterWeb = (filter: UserFilter) => {
    setFilters((prevState) => ({
      ...prevState,
      [filter.name]: filter.value,
    }))
  }

  const resetSpecificWebFilter = (filterName: UserFiltersName) => {
    updateFilterWeb({ name: filterName, value: initialFilters[filterName] })
  }

  const resetAllFilters = () => {
    setFilters(initialFilters)
  }

  const exportButton: HeaderButton = {
    label: i18n.t('screens.admin.users.buttons.export'),
    onClick: () => setExporting(true),
    color: 'light',
    status: displayedData.length === 0 ? 'disabled' : exporting ? 'loading' : 'active',
  }

  const addUserButton: HeaderButton = {
    label: i18n.t('screens.admin.users.buttons.addUser'),
    onClick: () => navigation.push('/admin/users/form/info'),
  }

  const displayExport = true // TODO : intégrer les scopes

  const headerButtons: HeaderButton[] = displayExport ? [exportButton, addUserButton] : [addUserButton]

  const renderUserCard = ({ item }: { item: UserResponse }) => (
    <UserContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/users/${item.id}`, { userId: item.id })}>
      <UserCard user={item} />
    </UserContainer>
  )

  const renderHeader = () => {
    if (isPhone) {
      return (
        <UserListHeader
          onFilter={openFilterMobile}
          onExport={exportButton.onClick}
          setSearch={setSearch}
          searchText={search}
          onLeft={() => navigation.goBack()}
          displayExport={displayExport}
          exportStatus={exportButton.status}
          numberOfFilters={numberOfFilters}
        />
      )
    }
  }

  const openFilterMobile = () => {
    Drawer.open({
      render: () => (
        <UserFiltersMobile
          onClose={() => Drawer.close()}
          onApplyFilter={setFilters}
          filters={filters}
          filterItems={filterItems}
          onReset={resetAllFilters}
          initialFilters={initialFilters}
        />
      ),
      hideTarget: true,
    })
  }

  const filterItems: UserFilterItems = React.useMemo(
    () => ({
      manifestation: manifestations
        .map((m) => ({ name: m.libelle, id: m.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      prestataire: services
        .map((s) => ({ name: s.nameSociete, id: s.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      role: roles.map((r) => ({ name: r.name, id: r.id })).sort((a, b) => a.name.localeCompare(b.name)),
      site: sites
        .map((s) => ({ name: i18n.t(`screens.site.sites.${s.codeLieu}`), id: s.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      status: [
        { name: i18n.t('screens.admin.users.filters.values.active'), id: ACTIVE_FILTER_ID },
        { name: i18n.t('screens.admin.users.filters.values.inactive'), id: INACTIVE_FILTER_ID },
      ],
      typeUtilisateur: USER_TYPES.map((key) => ({ name: key, id: key })),
    }),
    [sites, manifestations, roles, services]
  )

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? i18n.t('screens.admin.users.title') : undefined}
      renderSubHeader={isPhone ? undefined : renderHeader}
      renderHeader={isPhone ? renderHeader : undefined}
      renderFixedBottom={() => (isPhone ? renderBottomButton(addUserButton) : null)}
      fixed={isPhone}>
      {!isPhone && (
        <HeaderContainer>
          <Header
            title={i18n.t('screens.admin.users.title')}
            buttons={headerButtons}
            renderBottom={() => (
              <UserListFilters
                onFilterSelected={updateFilterWeb}
                onReset={resetAllFilters}
                filters={filters}
                filterItems={filterItems}
                resetSpecificFilter={resetSpecificWebFilter}
                onSearchTextChange={setSearch}
                searchText={search}
                initialFilters={initialFilters}
                setIsFilterOpen={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
                onHover={setIsHoverFilter}
              />
            )}
          />
        </HeaderContainer>
      )}
      <SortContainer>
        <SortSelector
          onOrderChange={() => setReverse(!reverse)}
          onSortChange={setSort}
          reverse={reverse}
          sorts={sorts}
          sort={sort}
        />
      </SortContainer>
      <FlatList
        data={displayedData}
        renderItem={renderUserCard}
        contentContainerStyle={{
          flexWrap: 'wrap',
          flexDirection: isPhone ? 'column' : 'row',
          marginVertical: 20,
          marginHorizontal: isPhone ? 16 : 25,
          justifyContent: isPhone ? 'center' : 'flex-start',
        }}
        ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.users.empty')}</MissingInfo>}
        showsVerticalScrollIndicator={false}
      />
    </Root>
  )
}

export default UserListScreen

const Footer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const UserContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SortContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint !== 'phone' ? `16px 0 0 24px` : `10px 16px`)};
`
